import { Check } from "flowbite-react-icons/outline";
import { useCallback, useMemo, useRef, useState } from "preact/hooks";
import PropTypes from "prop-types";
import "./color-picker.scss";
import { COLOR_VALUES } from "@/data/colors.js";
import { formatColorName } from "@/util/common.js";

const defaultFilterFunction = (color) => !!color;

export default function ColorPicker({
  onChange,
  currentColor,
  fieldName,
  filterFunction = defaultFilterFunction,
  allowMulti = false,
  lightColorsOnly = false,
  disabled = false,
}) {
  const colorInputRef = useRef(null);

  const [pickerState, setPickerState] = useState({
    color: currentColor,
  });

  const changeColor = useCallback((color) => {
    setPickerState({ color });
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    ).set;

    nativeInputValueSetter.call(colorInputRef.current, color);

    const event = new Event("input", { bubbles: true });
    colorInputRef.current.dispatchEvent(event);
  }, []);

  let validColors = allowMulti ? ["multi", ...COLOR_VALUES] : COLOR_VALUES;

  if (lightColorsOnly) {
    validColors = validColors
      .filter((color) => !color.includes("dark"))
      .map((color) => color.replace("light-", ""));
  }

  const isLightGray = currentColor === "light-grey";

  const colorOptions = useMemo(
    () =>
      validColors.filter(filterFunction).map((color) => {
        if (color === currentColor) {
          return (
            <button
              type="button"
              disabled={disabled}
              key={color}
              onClick={() => changeColor(color)}
              className={`h-10 w-10 flex border-2 border-base-300 justify-center content-center ${isLightGray ? "text-neutral-600" : "text-white"}  ${disabled ? "opacity-50" : "opacity-100"} items-center rounded-lg color-picker-${color} mr-2`}
            >
              <Check />
            </button>
          );
        } else {
          return (
            <div
              key={color}
              data-tip={formatColorName(color)}
              className={disabled ? "z-20" : "tooltip tooltip-top z-20"}
            >
              <button
                type="button"
                disabled={disabled}
                onClick={() => changeColor(color)}
                className={`h-10 w-10 border-2 transition-opacity border-base-300 rounded-lg color-picker-${color} ${disabled ? "opacity-50" : "opacity-100"} mr-2`}
              ></button>
            </div>
          );
        }
      }),
    [validColors, currentColor, filterFunction, disabled, changeColor, isLightGray],
  );

  return (
    <div className="flex flex-wrap md:flex-nowrap pl-1 pr-1">
      <input
        className="hidden"
        name={fieldName}
        value={pickerState.color}
        onChange={onChange}
        ref={colorInputRef}
      />
      {colorOptions}
    </div>
  );
}

ColorPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  currentColor: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  filterFunction: PropTypes.func,
  allowMulti: PropTypes.bool,
  lightColorsOnly: PropTypes.bool,
  disabled: PropTypes.bool,
};
